import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../components/Logo';

export function OtpEmailScreen({ onSubmit }: { onSubmit: (email: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Wrapper title={t('iframe.otp.email.title')} body={t('iframe.otp.email.body')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('iframe.otp.email.placeholder')}
          className="block h-11 w-full rounded-lg border border-line bg-surface-white px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
        />
        {error && <p className="text-xs text-bad">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-bit2me px-4 text-sm font-medium text-white transition-colors hover:bg-bit2me-mid disabled:bg-ink-muted"
        >
          {busy ? t('iframe.common.working') : t('iframe.otp.email.submit')}
        </button>
      </form>
    </Wrapper>
  );
}

export function OtpCodeScreen({
  email,
  onSubmit,
  onResend,
}: {
  email: string;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Wrapper title={t('iframe.otp.code.title')} body={t('iframe.otp.code.body', { email })}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          autoComplete="one-time-code"
          required
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          className="block h-11 w-full rounded-lg border border-line bg-surface-white px-3 text-center font-mono text-base tracking-[0.3em] text-ink-primary placeholder:text-ink-muted focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
        />
        {error && <p className="text-xs text-bad">{error}</p>}
        <button
          type="submit"
          disabled={busy || code.length !== 6}
          className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-bit2me px-4 text-sm font-medium text-white transition-colors hover:bg-bit2me-mid disabled:bg-ink-muted"
        >
          {busy ? t('iframe.common.working') : t('iframe.otp.code.submit')}
        </button>
        <button
          type="button"
          onClick={onResend}
          className="text-xs text-ink-secondary underline-offset-4 hover:text-ink-primary hover:underline"
        >
          {t('iframe.otp.code.resend')}
        </button>
      </form>
    </Wrapper>
  );
}

function Wrapper({ title, body, children }: { title: string; body: string; children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <Logo variant="mark" size={36} />
          </div>
          <h1 className="text-lg font-semibold tracking-tightish text-ink-primary">{title}</h1>
          <p className="mt-1 text-sm leading-relaxed text-ink-secondary">{body}</p>
        </div>
        {children}
      </div>
    </main>
  );
}
